import cardsService from "../services/cards.services";
import clientmanagmentObj from "../services/client-managment.service";
import depositService from "../services/deposit.service";
import transactionService from "../services/transaction.service";
import cardHolderService from "../services/cardholder.service";

/**
 * Bo Agent — tool registry.
 *
 * This is the layer that matters long term. Each tool is declared the way an
 * MCP / function-calling model expects: a name, a description, the phrases
 * that should trigger it, and a `run` that returns a structured answer.
 *
 * Today a keyword matcher picks the tool (see resolveTool below). When a real
 * model is wired in, the SAME registry is handed over as the tool schema and
 * the model picks instead — no tool has to be rewritten, and the UI does not
 * change at all. That is the whole point of structuring it this way rather
 * than hardcoding replies in the component.
 *
 * Every tool is READ-ONLY. Nothing here mutates data, so the agent cannot
 * suspend a card or approve a deposit by accident or by prompt injection.
 * Write actions would need a deliberate, separately-reviewed design with a
 * confirmation step.
 */

const fmt = (n) => Number(n ?? 0).toLocaleString();

const asDate = (d) => {
  if (!d) return "—";
  const t = new Date(d);
  return Number.isNaN(t.getTime())
    ? "—"
    : t.toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });
};

/* Token IDs in this system are long digit strings (10+). Pull one out of a
   free-text question so "look up token 2730855274" and a bare paste both
   work. Deliberately strict: a short number in "how many cards" must not be
   mistaken for a token. */
export function extractTokenId(text) {
  const m = String(text || "").match(/\b(\d{8,})\b/);
  return m ? m[1] : null;
}

/* each tool returns { text, detail?, link?, linkLabel? } */
export const TOOLS = [
  {
    name: "get_card_stats",
    description:
      "Total, active and inactive card counts across the platform.",
    keywords: ["card", "cards", "active card", "inactive card", "how many cards"],
    run: async ({ clientId }) => {
      const r = await cardsService.getCardStatusData(clientId);
      const res = r?.data?.payload?.result ?? {};
      const active = Number(res.Active ?? 0);
      const inactive = Number(res.InActive ?? 0);
      const total = active + inactive;
      const pct = total > 0 ? ((active / total) * 100).toFixed(1) : "0";
      return {
        text: `There are ${fmt(total)} cards on the platform.`,
        detail: [
          { label: "Active", value: fmt(active) },
          { label: "Inactive", value: fmt(inactive) },
          { label: "Active rate", value: pct + "%" },
        ],
        link: "/cards",
        linkLabel: "Open Cards",
      };
    },
  },
  {
    name: "get_client_stats",
    description: "How many clients exist, and how many are suspended.",
    keywords: ["client", "clients", "how many clients", "suspended client"],
    run: async () => {
      const r = await clientmanagmentObj.getclientcount();
      const res = r?.data?.payload?.result ?? {};
      const total = Number(res.totalClient ?? 0);
      const suspended = Number(res.inActiveClient ?? 0);
      return {
        text:
          suspended > 0
            ? `You have ${fmt(total)} clients, ${fmt(suspended)} of them suspended.`
            : `You have ${fmt(total)} clients, none currently suspended.`,
        detail: [
          { label: "Total", value: fmt(total) },
          { label: "Active", value: fmt(res.activeClient) },
          { label: "Suspended", value: fmt(suspended) },
        ],
        link: "/clients",
        linkLabel: "Open Clients",
      };
    },
  },
  {
    name: "get_pending_deposits",
    description: "Deposits currently awaiting approval.",
    keywords: ["deposit", "deposits", "pending deposit", "awaiting approval", "approve"],
    run: async () => {
      const r = await depositService.getDepositCounts(
        "", "Pending", "0001-01-01", "0001-01-01", "0001-01-01", "0001-01-01", "", 1, ""
      );
      const n = Number(r?.data?.payload?.result?.totalCount ?? 0);
      return {
        text:
          n === 0
            ? "No deposits are waiting for approval right now."
            : `${fmt(n)} deposit${n === 1 ? " is" : "s are"} awaiting approval.`,
        link: n > 0 ? "/deposit" : null,
        linkLabel: "Review deposits",
      };
    },
  },
  {
    name: "get_pin_requests",
    description: "Outstanding PIN change requests.",
    keywords: ["pin", "pin change", "change pin", "pin request"],
    run: async () => {
      const r = await clientmanagmentObj.getChangePinCount(
        "", "", "0001-01-01T00:00:00.000Z", "0001-01-01T00:00:00.000Z", "", 1
      );
      const n = Number(r?.data?.payload?.result?.totalCount ?? 0);
      return {
        text:
          n === 0
            ? "There are no outstanding PIN change requests."
            : `${fmt(n)} PIN change request${n === 1 ? "" : "s"} outstanding.`,
        link: n > 0 ? "/changepins" : null,
        linkLabel: "Review requests",
      };
    },
  },
  {
    name: "get_transaction_stats",
    description: "Transaction volume and averages.",
    keywords: ["transaction", "volume", "revenue", "amount", "money", "spend"],
    run: async ({ clientId }) => {
      const r = await transactionService.getTransactionStatsData(clientId);
      const res = r?.data?.payload?.result ?? {};
      return {
        text: `Total transaction volume is ${fmt(res.totalAmount)}.`,
        detail: [
          { label: "Volume", value: fmt(res.totalAmount) },
          { label: "Avg. transactions", value: fmt(res.averageTransactions) },
          { label: "Transacting users", value: fmt(res.totalUsers) },
        ],
        link: "/cards",
        linkLabel: "Open Cards",
      };
    },
  },
  {
    name: "get_platform_health",
    description:
      "Recent API activity and the success rate across those calls.",
    keywords: ["health", "status", "error", "errors", "failing", "api", "logs", "activity"],
    run: async () => {
      const r = await clientmanagmentObj.searchLogs(null, null, null, 1, 50, null);
      const rows = Array.isArray(r?.data)
        ? r.data
        : r?.data?.result || r?.data?.payload || [];
      const sample = Array.isArray(rows) ? rows.slice(0, 50) : [];
      if (sample.length === 0) {
        return { text: "I could not read any recent API activity.", link: "/logrecords", linkLabel: "Open Log records" };
      }
      const ok = sample.filter(
        (l) => Number(l?.statusCode) >= 200 && Number(l?.statusCode) < 400
      ).length;
      const rate = ((ok / sample.length) * 100).toFixed(0);
      const failing = sample.length - ok;
      return {
        text:
          failing === 0
            ? `All ${sample.length} recent API calls succeeded (${rate}% OK).`
            : `${rate}% of the last ${sample.length} API calls succeeded — ${failing} failing.`,
        detail: [
          { label: "Sampled", value: String(sample.length) },
          { label: "Succeeded", value: String(ok) },
          { label: "Failed", value: String(failing) },
        ],
        link: "/logrecords",
        linkLabel: "Open Log records",
      };
    },
  },
  {
    name: "lookup_card_by_token",
    description:
      "Look up a single card and its holder by Token ID. Takes a tokenId parameter.",
    keywords: ["token", "token id", "look up", "lookup", "find card", "card details"],
    /* The only tool that takes a PARAMETER — the shape a real model would
       call with {"tokenId": "..."}. Today the id is pulled from the question
       text by extractTokenId; a model would supply it as an argument and the
       body below would not change. */
    paramHint: "tokenId",
    run: async ({ question, tokenId }) => {
      const token = tokenId || extractTokenId(question);
      if (!token) {
        return {
          text:
            "Give me a Token ID and I'll pull up the card — for example “look up token 2730855274”.",
        };
      }

      // step 1 — token -> cardholder uuid + userId
      let uuid;
      let userId;
      let issuerCategory;
      try {
        const r = await transactionService.getCardHolderUuid(token);
        const p = r?.data?.payload;
        uuid = p?.cardHolderUuid;
        userId = p?.userId;
        issuerCategory = p?.isuerCategory;
      } catch (e) {
        return { text: `I couldn't look up token ${token} just now. Please try again.` };
      }

      if (!uuid && !userId) {
        return {
          text: `I couldn't find a card with Token ID ${token}. Double-check the number?`,
        };
      }

      // step 2 — fetch holder and card in parallel
      const [holderRes, cardRes] = await Promise.all([
        uuid
          ? cardHolderService.getcardholderDetail(uuid).catch(() => null)
          : Promise.resolve(null),
        userId
          ? cardHolderService.getCardDetail(userId).catch(() => null)
          : Promise.resolve(null),
      ]);

      const holder = holderRes?.data?.payload?.result ?? {};
      const card = cardRes?.data?.payload?.result ?? {};

      const name =
        [holder.firstName, holder.lastName].filter(Boolean).join(" ") ||
        holder.cardHolderName ||
        "this cardholder";

      // card.issuspended is inverted elsewhere in the app; here we read the
      // raw flags straight from the API rather than the UI's Status variable
      const suspended = card.issuspended === true;
      const live = card.isActive === true;
      const state = suspended ? "Suspended" : live ? "Active" : "Inactive";

      return {
        text: `Token ${token} belongs to ${name} — the card is ${state.toLowerCase()}.`,
        detail: [
          { label: "Cardholder", value: name },
          { label: "Status", value: state },
          { label: "Card", value: card.maskedCardNumber || "—" },
          { label: "Type", value: card.cardTypeName || "—" },
          {
            label: "Balance",
            value:
              holder.availableBalance !== undefined
                ? fmt(holder.availableBalance) +
                  (card.cardFeeCurrencyCode ? " " + card.cardFeeCurrencyCode : "")
                : "—",
          },
          { label: "Client", value: holder.companyName || "—" },
          { label: "Issued", value: asDate(card.issueDate) },
          { label: "Expires", value: asDate(card.expiryDate) },
        ],
        link: "/cardholderdetails",
        linkLabel: "Open card holder",
        /* /cardholderdetails reads these from router state, exactly as the
           grids pass them when a row is clicked */
        linkState: { id: uuid, userId, issuerCategory },
      };
    },
  },
  {
    name: "navigate",
    description: "Point the operator at the right screen.",
    keywords: ["where", "find", "open", "go to", "show me", "take me"],
    run: async ({ question }) => {
      const map = [
        [/card/i, "/cards", "Cards"],
        [/client/i, "/clients", "Clients"],
        [/deposit/i, "/deposit", "Deposits"],
        [/pin/i, "/changepins", "Change Pin"],
        [/log|audit/i, "/logrecords", "Log Records"],
        [/role|permission|user/i, "/rolemanagment", "Role Management"],
        [/fee/i, "/feemanagments", "Fee Management"],
        [/decline/i, "/declinetransactions", "Decline Transactions"],
        [/api|key/i, "/apikeymanagment", "API Key Management"],
      ];
      const hit = map.find(([re]) => re.test(question));
      if (!hit) {
        return { text: "Which screen are you looking for? Try naming cards, clients, deposits, fees or logs." };
      }
      return { text: `${hit[2]} is on the left.`, link: hit[1], linkLabel: `Open ${hit[2]}` };
    },
  },
];

/**
 * Pick a tool for a question.
 *
 * Deliberately simple: score each tool by how many of its keywords appear in
 * the question, longest keyword first so "pending deposit" beats "deposit".
 * This is the ONLY piece a real model replaces — everything above stays.
 */
export function resolveTool(question) {
  const q = (question || "").toLowerCase().trim();
  if (!q) return null;

  /* A long digit string is an unambiguous signal: the operator has a
     specific card in mind. Without this, "card details for 2730855274"
     would match get_card_stats on the word "card" and answer with
     platform-wide totals — confidently, and wrongly. */
  if (extractTokenId(q)) {
    return TOOLS.find((t) => t.name === "lookup_card_by_token");
  }

  let best = null;
  let bestScore = 0;

  for (const tool of TOOLS) {
    let score = 0;
    for (const kw of tool.keywords) {
      if (q.includes(kw)) score = Math.max(score, kw.length);
    }
    // "navigate" is a fallback intent — only win on an explicit phrase
    if (tool.name === "navigate" && score > 0 && score < 5) score = 0;
    if (score > bestScore) {
      bestScore = score;
      best = tool;
    }
  }
  return best;
}

export const SUGGESTIONS = [
  "How many cards are active?",
  "Any deposits awaiting approval?",
  /* no hardcoded token here — a baked-in id would 404 in any environment
     that does not happen to have it, which is the worst thing that can
     happen in a demo. The empty prompt invites a real paste instead. */
  "Look up a card by token ID",
  "Is the platform healthy?",
  "How many clients do we have?",
];
